/*
 * Set Selector
 * ------------
 * Tier -> product picker rendered as blocks inside the product form.
 *
 * Tiers stay collapsed until the customer clicks one. Opening a tier
 * collapses the others and selects its first in-stock option, writing that
 * variant id into the theme's native <select name="id"> so the theme's own
 * Add to cart button adds the chosen set. There is no second button.
 *
 * Selecting a set also swaps the product gallery for that set's own images.
 * The theme's gallery is a Flickity slideshow built from THIS product's
 * media, and a set is a different product, so its images cannot be slides.
 * Instead a self-contained carousel (stage + arrows + counter + thumbnails,
 * with swipe and keyboard support) is layered over the main photo area, and
 * the theme's own thumbnail strip is hidden while it is showing. Collapsing
 * the tier restores the original gallery untouched.
 */
(function () {
  'use strict';

  var SIZES = '(min-width: 750px) 50vw, 100vw';
  var BASE_W = 1400; // width baked into data-gallery URLs by the Liquid snippet
  var THUMB_W = 200;

  function atWidth(url, w) {
    return url.replace('width=' + BASE_W, 'width=' + w);
  }

  function srcsetFor(url) {
    return [400, 600, 900, 1400]
      .map(function (w) {
        return atWidth(url, w) + ' ' + w + 'w';
      })
      .join(', ');
  }

  function parseGallery(card) {
    var raw = (card.dataset.gallery || '').trim();
    if (!raw) {
      return card.dataset.hero ? [card.dataset.hero] : [];
    }
    return raw
      .split('|')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function group(tiers) {
    if (!tiers.length) return;

    var formId = tiers[0].dataset.formId || '';
    var form =
      (formId && document.getElementById(formId)) ||
      tiers[0].closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    var select = form ? form.querySelector('select[name="id"], [data-product-select]') : null;
    var initialVariant = select ? select.value : null;

    var scope = tiers[0].closest('.shopify-section') || document;
    var gal = buildGallery(scope);

    var footer = buildFooter(tiers[tiers.length - 1]);
    var openTierEl = null;

    tiers.forEach(function (tier) {
      var head = tier.querySelector('.set-tier__head');
      var radio = tier.querySelector('[data-tier-radio]');

      if (head) {
        head.addEventListener('click', function (evt) {
          evt.preventDefault();
          if (openTierEl === tier) closeAll();
          else open(tier);
        });
      }

      if (radio) {
        radio.addEventListener('keydown', function (evt) {
          if (evt.key === ' ' || evt.key === 'Enter') {
            evt.preventDefault();
            if (openTierEl === tier) closeAll();
            else open(tier);
          }
        });
      }

      tier.querySelectorAll('[data-set-card]').forEach(function (card) {
        card.addEventListener('click', function () {
          if (card.disabled) return;
          selectCard(tier, card);
        });
        card.addEventListener('pointerenter', function () {
          prefetchCard(card);
        });
        card.addEventListener('focus', function () {
          prefetchCard(card);
        });
      });
    });

    function open(tier) {
      openTierEl = tier;
      prefetchTier(tier);

      tiers.forEach(function (t) {
        var list = t.querySelector('[data-tier-products]');
        var radio = t.querySelector('[data-tier-radio]');
        var isActive = t === tier;

        t.classList.toggle('set-tier--open', isActive);
        if (list) list.hidden = !isActive;
        if (radio) radio.checked = isActive;

        if (!isActive) clearCards(t);
      });

      var already = tier.querySelector('[data-set-card].set-card--selected');
      if (already) {
        apply(already);
        return;
      }
      var first = tier.querySelector('[data-set-card]:not([disabled])');
      if (first) selectCard(tier, first);
      else apply(null);
    }

    function closeAll() {
      openTierEl = null;
      tiers.forEach(function (t) {
        var list = t.querySelector('[data-tier-products]');
        var radio = t.querySelector('[data-tier-radio]');
        t.classList.remove('set-tier--open');
        if (list) list.hidden = true;
        if (radio) radio.checked = false;
        clearCards(t);
      });
      restore();
      updateFooter(null);
      gal.hide();
    }

    function clearCards(tier) {
      tier.querySelectorAll('[data-set-card]').forEach(function (card) {
        card.classList.remove('set-card--selected');
        card.setAttribute('aria-pressed', 'false');
      });
    }

    function selectCard(tier, card) {
      clearCards(tier);
      card.classList.add('set-card--selected');
      card.setAttribute('aria-pressed', 'true');
      apply(card);
    }

    function apply(card) {
      updateFooter(card);
      gal.show(card);
      if (!card || !select) return;

      var id = String(card.dataset.variantId || '');
      if (!id) return;

      var opt = select.querySelector('option[value="' + id + '"]');
      if (!opt) {
        opt = document.createElement('option');
        opt.value = id;
        var title = card.querySelector('.set-card__title');
        opt.textContent = title ? title.textContent : id;
        opt.dataset.setSelectorInjected = 'true';
        select.appendChild(opt);
      }
      select.value = id;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function restore() {
      if (!select || initialVariant == null) return;
      select.value = initialVariant;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function prefetchCard(card) {
      if (!card || card.dataset.heroPrefetched === 'true') return;
      var urls = parseGallery(card);
      if (!urls.length) return;
      card.dataset.heroPrefetched = 'true';
      var i = new Image();
      i.decoding = 'async';
      i.sizes = SIZES;
      i.srcset = srcsetFor(urls[0]);
      i.src = urls[0];
    }

    function prefetchTier(tier) {
      var cards = Array.prototype.slice.call(tier.querySelectorAll('[data-set-card]')).slice(0, 5);
      var run = function () {
        cards.forEach(prefetchCard);
      };
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 200);
    }

    function buildFooter(lastTier) {
      var el = document.createElement('div');
      el.className = 'set-selector__total product-block';
      el.hidden = true;
      el.innerHTML =
        '<span class="set-selector__total-label">Selected</span>' +
        '<span class="set-selector__total-price" data-total-price></span>' +
        '<s class="set-selector__total-was" data-total-was hidden></s>';
      if (lastTier && lastTier.parentNode) {
        lastTier.parentNode.insertBefore(el, lastTier.nextSibling);
      }
      return el;
    }

    function updateFooter(card) {
      if (!footer) return;
      if (!card) {
        footer.hidden = true;
        return;
      }
      footer.hidden = false;
      var priceEl = footer.querySelector('[data-total-price]');
      var wasEl = footer.querySelector('[data-total-was]');
      if (priceEl) priceEl.textContent = card.dataset.priceFormatted || '';
      if (wasEl) {
        var compare = parseInt(card.dataset.compare, 10) || 0;
        var price = parseInt(card.dataset.price, 10) || 0;
        if (compare > price) {
          wasEl.textContent = card.dataset.compareFormatted || '';
          wasEl.hidden = false;
        } else {
          wasEl.hidden = true;
        }
      }
    }

    closeAll();
    var defaultOpen = null;
    tiers.forEach(function (t) {
      if (!defaultOpen && t.querySelector('[data-tier-radio][data-tier-preselect]')) defaultOpen = t;
    });
    if (defaultOpen) open(defaultOpen);
  }

  /* Gallery -------------------------------------------------------- */

  function buildGallery(root) {
    var host = root.querySelector('[data-product-single-media-group]');
    var images = root.querySelector('[data-product-images]');

    if (!host) {
      return { show: function () {}, hide: function () {} };
    }

    var el = document.createElement('div');
    el.className = 'set-gallery';
    el.hidden = true;
    el.innerHTML =
      '<div class="set-gallery__stage">' +
      '<img class="set-gallery__img" alt="" />' +
      '<button type="button" class="set-gallery__nav set-gallery__nav--prev" aria-label="Previous image"></button>' +
      '<button type="button" class="set-gallery__nav set-gallery__nav--next" aria-label="Next image"></button>' +
      '<span class="set-gallery__counter" aria-live="polite"></span>' +
      '</div>' +
      '<div class="set-gallery__thumbs" role="list"></div>';

    host.classList.add('set-gallery-host');
    host.appendChild(el);

    var img = el.querySelector('.set-gallery__img');
    var prev = el.querySelector('.set-gallery__nav--prev');
    var next = el.querySelector('.set-gallery__nav--next');
    var counter = el.querySelector('.set-gallery__counter');
    var thumbs = el.querySelector('.set-gallery__thumbs');

    var urls = [];
    var title = '';
    var index = 0;
    var token = 0;

    prev.addEventListener('click', function () {
      go(index - 1);
    });
    next.addEventListener('click', function () {
      go(index + 1);
    });

    // Swipe
    var startX = null;
    el.addEventListener(
      'touchstart',
      function (e) {
        startX = e.touches[0].clientX;
      },
      { passive: true }
    );
    el.addEventListener(
      'touchend',
      function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );

    // Keyboard, only while the carousel is showing.
    document.addEventListener('keydown', function (e) {
      if (el.hidden) return;
      if (e.key === 'ArrowLeft') {
        go(index - 1);
      } else if (e.key === 'ArrowRight') {
        go(index + 1);
      }
    });

    function renderThumbs() {
      thumbs.innerHTML = '';
      if (urls.length < 2) return;
      urls.forEach(function (u, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'set-gallery__thumb';
        b.setAttribute('role', 'listitem');
        b.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + urls.length);
        b.innerHTML = '<img src="' + atWidth(u, THUMB_W) + '" alt="" loading="lazy" />';
        b.addEventListener('click', function () {
          go(i);
        });
        thumbs.appendChild(b);
      });
    }

    function markThumb() {
      var all = thumbs.querySelectorAll('.set-gallery__thumb');
      for (var i = 0; i < all.length; i++) {
        var on = i === index;
        all[i].classList.toggle('set-gallery__thumb--active', on);
        all[i].setAttribute('aria-current', on ? 'true' : 'false');
      }
      var active = all[index];
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    function go(i) {
      if (!urls.length) return;
      // Wrap around so arrows never dead-end.
      index = ((i % urls.length) + urls.length) % urls.length;

      token += 1;
      var mine = token;
      var url = urls[index];

      // Paint the small thumbnail immediately, then swap in the full size.
      var placeholder = atWidth(url, THUMB_W);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = placeholder;
      img.alt = title ? title + ' - image ' + (index + 1) : '';
      el.classList.add('set-gallery--loading');

      var pre = new Image();
      pre.decoding = 'async';
      if ('fetchPriority' in pre) pre.fetchPriority = 'high';
      pre.sizes = SIZES;
      pre.srcset = srcsetFor(url);
      pre.src = url;

      var paint = function () {
        if (mine !== token) return;
        img.sizes = SIZES;
        img.srcset = srcsetFor(url);
        img.src = url;
        el.classList.remove('set-gallery--loading');
      };
      if (pre.complete) paint();
      else {
        pre.addEventListener('load', paint);
        pre.addEventListener('error', function () {
          if (mine === token) el.classList.remove('set-gallery--loading');
        });
      }

      if (counter) {
        counter.textContent = urls.length > 1 ? index + 1 + ' / ' + urls.length : '';
      }
      var multi = urls.length > 1;
      prev.hidden = !multi;
      next.hidden = !multi;
      markThumb();

      // Warm the neighbours so arrow taps feel instant.
      [index + 1, index - 1].forEach(function (n) {
        var j = ((n % urls.length) + urls.length) % urls.length;
        if (j === index) return;
        var w = new Image();
        w.decoding = 'async';
        w.src = urls[j];
      });
    }

    return {
      show: function (card) {
        if (!card) {
          this.hide();
          return;
        }
        var list = parseGallery(card);
        if (!list.length) {
          this.hide();
          return;
        }
        urls = list;
        title = card.dataset.galleryTitle || card.dataset.heroAlt || '';
        el.hidden = false;
        if (images) images.classList.add('set-gallery-active');
        renderThumbs();
        go(0);
      },
      hide: function () {
        token += 1;
        el.hidden = true;
        el.classList.remove('set-gallery--loading');
        if (images) images.classList.remove('set-gallery-active');
      }
    };
  }

  function boot() {
    var all = Array.prototype.slice.call(document.querySelectorAll('[data-set-tier]'));
    var fresh = all.filter(function (t) {
      if (t.dataset.setTierReady === 'true') return false;
      t.dataset.setTierReady = 'true';
      return true;
    });
    if (!fresh.length) return;

    var byForm = {};
    fresh.forEach(function (t) {
      var key = t.dataset.formId || 'default';
      (byForm[key] = byForm[key] || []).push(t);
    });
    Object.keys(byForm).forEach(function (k) {
      group(byForm[k]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
